import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, useSearchParams } from "./lib/router";
import AppShell from "./components/AppShell";

const FoundationPage = lazy(() => import("./routes/FoundationPage"));
const AcademicContextPage = lazy(() => import("./routes/AcademicContextPage"));
const StudentImportUploadPage = lazy(() => import("./routes/StudentImportUploadPage"));
const StudentIdentityCorrectionPage = lazy(() => import("./routes/StudentIdentityCorrectionPage"));
const StudentRosterPage = lazy(() => import("./routes/StudentDirectoryPage"));
const TeacherMasterPage = lazy(() => import("./routes/TeacherMasterPage"));
const TeachingPlanPage = lazy(() => import("./routes/TeachingPlanPage"));
const CourseRosterPage = lazy(() => import("./routes/CourseRosterPage"));
const AttendancePage = lazy(() => import("./routes/AttendancePage"));
const AttendanceCompliancePage = lazy(() => import("./routes/AttendanceCompliancePage"));
const NotificationPage = lazy(() => import("./routes/NotificationPage"));
const SubstitutionPage = lazy(() => import("./routes/SubstitutionPage"));
const SubstitutionAdminPage = lazy(() => import("./routes/SubstitutionAdminPage"));
const WorkspaceHomePage = lazy(() => import("./routes/WorkspaceHomePage"));
const BehaviorCategoriesPage = lazy(() => import("./routes/BehaviorCategoriesPage"));
const BehaviorRecordPage = lazy(() => import("./routes/BehaviorRecordPage"));
const BehaviorSummaryPage = lazy(() => import("./routes/BehaviorSummaryPage"));
const ScoreSetupPage = lazy(() => import("./routes/ScoreSetupPage"));
const ScoreEntryPage = lazy(() => import("./routes/ScoreEntryPage"));
const ScoreReviewQueuePage = lazy(() => import("./routes/ScoreReviewQueuePage"));
const ScoreCorrectionQueuePage = lazy(() => import("./routes/ScoreCorrectionQueuePage"));
const StudentResultsPage = lazy(() => import("./routes/StudentResultsPage"));
const StudentAccountReadinessPage = lazy(() => import("./routes/StudentAccountReadinessPage"));
const ResultReleaseReadinessPage = lazy(() => import("./routes/ResultReleaseReadinessPage"));
const ResultReportsPage = lazy(() => import("./routes/ResultReportsPage"));
const Pp5SettingsPage = lazy(() => import("./routes/Pp5SettingsPage"));
const YearRolloverPage = lazy(() => import("./routes/YearRolloverPage"));
const AdvisoryLeavePage = lazy(() => import("./routes/AdvisoryLeavePage"));
const ActivityAttendancePage = lazy(() => import("./routes/ActivityAttendancePage"));
const ActivityAttendanceSettingsPage = lazy(() => import("./routes/ActivityAttendanceSettingsPage"));
const ExamWorkspacePage = lazy(() => import("./routes/ExamWorkspacePage"));
const StudentExamPage = lazy(() => import("./routes/StudentExamPage"));
const StudentHomePage = lazy(() => import("./routes/StudentHomePage"));
const StudentTimetablePage = lazy(() => import("./routes/StudentTimetablePage"));
const ExamEssayGradingPage = lazy(() => import("./routes/ExamEssayGradingPage"));
const ExamProctorPage = lazy(() => import("./routes/ExamProctorPage"));
const ExamAnalysisPage = lazy(() => import("./routes/ExamAnalysisPage"));
const PersonalSettingsPage = lazy(() => import("./routes/PersonalSettingsPage"));
const AssignmentsPage = lazy(() => import("./routes/AssignmentsPage"));
const AssignmentReviewPage = lazy(() => import("./routes/AssignmentReviewPage"));
const StudentAssignmentsPage = lazy(() => import("./routes/StudentAssignmentsPage"));
const AssignmentFileAdminPage = lazy(() => import("./routes/AssignmentFileAdminPage"));

function RouteFallback() {
  return (
    <main className="route-state" aria-busy="true">
      <span className="spinner" aria-hidden="true" />
      <p>กำลังเตรียมพื้นที่ทำงาน…</p>
    </main>
  );
}

function LegacyAssignmentsRedirect() {
  const [searchParams] = useSearchParams();
  const query = searchParams.toString();
  return <Navigate to={`/scores/assignments${query ? `?${query}` : ""}`} replace />;
}

export const router = createBrowserRouter([
  { path: "/student/home", element: <Suspense fallback={<RouteFallback />}><StudentHomePage /></Suspense> },
  { path: "/student/timetable", element: <Suspense fallback={<RouteFallback />}><StudentTimetablePage /></Suspense> },
  { path: "/student/exams", element: <Suspense fallback={<RouteFallback />}><StudentExamPage /></Suspense> },
  { path: "/student/assignments", element: <Suspense fallback={<RouteFallback />}><StudentAssignmentsPage /></Suspense> },
  {
    element: <AppShell />,
    children: [
      { path: "/workspace", element: <Suspense fallback={<RouteFallback />}><WorkspaceHomePage /></Suspense> },
      { path: "/settings/personal", element: <Suspense fallback={<RouteFallback />}><PersonalSettingsPage /></Suspense> },
      { path: "/admin/substitutions", element: <Suspense fallback={<RouteFallback />}><SubstitutionAdminPage /></Suspense> },
      { path: "/substitutions", element: <Suspense fallback={<RouteFallback />}><SubstitutionPage /></Suspense> },
      { path: "/behavior/categories", element: <Suspense fallback={<RouteFallback />}><BehaviorCategoriesPage /></Suspense> },
      { path: "/behavior/record", element: <Suspense fallback={<RouteFallback />}><BehaviorRecordPage /></Suspense> },
      { path: "/behavior/summary", element: <Suspense fallback={<RouteFallback />}><BehaviorSummaryPage /></Suspense> },
      { path: "/scores/setup", element: <Suspense fallback={<RouteFallback />}><ScoreSetupPage /></Suspense> },
      { path: "/exams/bank", element: <Suspense fallback={<RouteFallback />}><ExamWorkspacePage /></Suspense> },
      { path: "/exams/essay-grading", element: <Suspense fallback={<RouteFallback />}><ExamEssayGradingPage /></Suspense> },
      { path: "/exams/proctor", element: <Suspense fallback={<RouteFallback />}><ExamProctorPage /></Suspense> },
      { path: "/exams/analysis", element: <Suspense fallback={<RouteFallback />}><ExamAnalysisPage /></Suspense> },
      { path: "/exams/catalog", element: <Navigate to="/exams/bank?step=catalog" replace /> },
      { path: "/scores/entry", element: <Suspense fallback={<RouteFallback />}><ScoreEntryPage /></Suspense> },
      { path: "/scores/assignments", element: <Suspense fallback={<RouteFallback />}><AssignmentsPage /></Suspense> },
      { path: "/scores/assignments/:assignmentId/:submissionId/review", element: <Suspense fallback={<RouteFallback />}><AssignmentReviewPage /></Suspense> },
      { path: "/assignments", element: <LegacyAssignmentsRedirect /> },
      { path: "/scores/review", element: <Suspense fallback={<RouteFallback />}><ScoreReviewQueuePage /></Suspense> },
      { path: "/scores/corrections", element: <Suspense fallback={<RouteFallback />}><ScoreCorrectionQueuePage /></Suspense> },
      { path: "/scores/readiness", element: <Suspense fallback={<RouteFallback />}><ResultReleaseReadinessPage /></Suspense> },
      { path: "/scores/reports", element: <Suspense fallback={<RouteFallback />}><ResultReportsPage /></Suspense> },
      { path: "/admin/settings/pp5", element: <Suspense fallback={<RouteFallback />}><Pp5SettingsPage /></Suspense> },
      { path: "/admin/master-data/year-rollover", element: <Suspense fallback={<RouteFallback />}><YearRolloverPage /></Suspense> },
      { path: "/admin/master-data/assignment-files", element: <Suspense fallback={<RouteFallback />}><AssignmentFileAdminPage /></Suspense> },
      { path: "/student/results", element: <Suspense fallback={<RouteFallback />}><StudentResultsPage /></Suspense> },
      { path: "/admin/master-data/student-accounts", element: <Suspense fallback={<RouteFallback />}><StudentAccountReadinessPage /></Suspense> },
      { path: "/notifications", element: <Suspense fallback={<RouteFallback />}><NotificationPage /></Suspense> },
      { path: "/attendance/compliance", element: <Suspense fallback={<RouteFallback />}><AttendanceCompliancePage /></Suspense> },
      { path: "/attendance", element: <Suspense fallback={<RouteFallback />}><AttendancePage /></Suspense> },
      { path: "/advisory/leaves", element: <Suspense fallback={<RouteFallback />}><AdvisoryLeavePage /></Suspense> },
      { path: "/activity-attendance", element: <Suspense fallback={<RouteFallback />}><ActivityAttendancePage /></Suspense> },
      { path: "/admin/master-data/activity-attendance", element: <Suspense fallback={<RouteFallback />}><ActivityAttendanceSettingsPage /></Suspense> },
      { path: "/admin/master-data/timetable", element: <Navigate to="/admin/master-data/courses?view=schedule" replace /> },
      { path: "/admin/master-data/courses/:courseId/roster", element: <Suspense fallback={<RouteFallback />}><CourseRosterPage /></Suspense> },
      { path: "/admin/master-data/courses", element: <Suspense fallback={<RouteFallback />}><TeachingPlanPage /></Suspense> },
      { path: "/admin/master-data/teachers", element: <Suspense fallback={<RouteFallback />}><TeacherMasterPage /></Suspense> },
      { path: "/admin/master-data/students/:studentId", element: <Navigate to="/admin/master-data/student-roster" replace /> },
      { path: "/admin/master-data/student-roster", element: <Suspense fallback={<RouteFallback />}><StudentRosterPage /></Suspense> },
      { path: "/admin/master-data/student-import/upload", element: <Suspense fallback={<RouteFallback />}><StudentImportUploadPage /></Suspense> },
      { path: "/admin/master-data/student-identity-corrections", element: <Suspense fallback={<RouteFallback />}><StudentIdentityCorrectionPage /></Suspense> },
      { path: "/admin/master-data/academic-context", element: <Suspense fallback={<RouteFallback />}><AcademicContextPage /></Suspense> },
      { path: "/admin/master-data/student-import", element: <Navigate to="/admin/master-data/student-import/upload" replace /> },
    ],
  },
  {
    path: "*",
    element: (
      <Suspense fallback={<RouteFallback />}>
        <FoundationPage />
      </Suspense>
    ),
  },
]);
